/**
 * API Client Configuration
 *
 * Centralized axios client with:
 * - Base URL configuration
 * - Request interceptors (auth tokens, etc.)
 * - Response interceptors (error handling)
 * - Type-safe error handling
 * - Pagination support
 * - Optional authentication skipping for public endpoints
 *
 * Usage:
 *   import { apiClient } from '@/api/client';
 *   const response = await apiClient.get('/endpoint');
 */
import axios, {
	AxiosError,
	AxiosRequestConfig,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from "axios";

import { markApiVerified } from "./api-verification-manager";
import { triggerLogout } from "./logout-manager";
import { getAccessToken } from "./token-manager";

const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

/**
 * Response type that includes pagination data if available
 */
export type ApiResponse<T> = {
	data: T;
	endCursor?: string;
	hasMore?: boolean;
	total?: number;
};

/**
 * Request configuration options (extends AxiosRequestConfig for compatibility)
 */
export interface RequestConfig extends AxiosRequestConfig {
	skipAuth?: boolean;
	timeout?: number;
}

/**
 * Axios response with pagination metadata attached
 */
interface AxiosResponseWithPagination<T> extends AxiosResponse<T> {
	__pagination?: {
		endCursor?: string;
		hasMore?: boolean;
		total?: number;
	};
}

/**
 * Custom API Error class for better error handling
 */
export class ApiError extends Error {
	constructor(
		message: string,
		public statusCode: number,
		public data?: unknown,
	) {
		super(message);
		this.name = "ApiError";
	}
}

/**
 * Create axios instance with base configuration
 */
const axiosInstance = axios.create({
	baseURL: BASE_URL,
	headers: {
		"Content-Type": "application/json",
	},
	timeout: 10000,
});

/**
 * Request interceptor - Add auth token to requests (unless skipAuth is true)
 */
axiosInstance.interceptors.request.use(
	async (config: InternalAxiosRequestConfig) => {
		// Skip authentication if skipAuth is set to true
		const skipAuth = (config as RequestConfig).skipAuth;
		if (!skipAuth) {
			// Get access token from Kinde via token manager
			const token = await getAccessToken();
			if (token && config.headers) {
				config.headers.Authorization = `Bearer ${token}`;
			}
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

/**
 * Response interceptor - Handle errors globally and extract pagination headers
 */
axiosInstance.interceptors.response.use(
	(response) => {
		// Mark API as verified on first successful response
		markApiVerified();

		// Extract pagination headers (axios normalizes headers to lowercase)
		// Try both lowercase and original case for compatibility
		const cursor =
			response.headers["x-end-cursor"] || response.headers["X-End-Cursor"];
		const hasMoreRaw =
			response.headers["x-has-more"] || response.headers["X-Has-More"];
		const total =
			response.headers["x-total-count"] || response.headers["X-Total-Count"];

		// Parse hasMore header - handle string "true"/"false", boolean, or undefined
		let hasMore: boolean | undefined;
		if (hasMoreRaw !== undefined && hasMoreRaw !== null) {
			if (typeof hasMoreRaw === "boolean") {
				hasMore = hasMoreRaw;
			} else {
				const hasMoreStr = String(hasMoreRaw).toLowerCase().trim();
				hasMore =
					hasMoreStr === "true"
						? true
						: hasMoreStr === "false"
							? false
							: undefined;
			}
		}

		// Attach pagination metadata to response object
		(response as AxiosResponseWithPagination<unknown>).__pagination = {
			endCursor: cursor || undefined,
			hasMore,
			total: total ? parseInt(String(total), 10) : undefined,
		};
		return response;
	},
	(error: AxiosError) => {
		// Handle specific error cases
		if (error.response) {
			const status = error.response.status;
			const skipAuth = (error.config as RequestConfig)?.skipAuth;

			if (status === 401 && !skipAuth) {
				// Unauthorized — token expired/invalid, drop the session.
				triggerLogout();
				throw new ApiError("Unauthorized", 401, error.response.data);
			} else if (status === 403 && !skipAuth) {
				// Forbidden — the user is authenticated, they just lack the
				// permission for this action. For UI purposes, we use a custom
				// error message via toasts per each react-query hook.
				throw new ApiError("Access forbidden", 403, error.response.data);
			} else if (status >= 500) {
				// Server errors
				console.error("Server error occurred");
				throw new ApiError(
					"Server error occurred",
					status,
					error.response.data,
				);
			}

			// Handle other error responses
			const errorData = error.response.data as { message?: string };
			throw new ApiError(
				errorData?.message || error.message || "Request failed",
				status,
				error.response.data,
			);
		} else if (error.request) {
			// Request was made but no response received
			if (error.code === "ECONNABORTED") {
				throw new ApiError("Request timeout", 408);
			}
			throw new ApiError("Network error", 0, error.message);
		} else {
			// Something else happened
			throw new ApiError(error.message || "An unexpected error occurred", 0);
		}
	},
);

/**
 * Helper function to construct ApiResponse from axios response
 */
function createApiResponse<T>(
	response: AxiosResponseWithPagination<T>,
): ApiResponse<T> {
	const pagination = response.__pagination || {};
	return {
		data: response.data,
		...(pagination.endCursor !== undefined && {
			endCursor: pagination.endCursor,
		}),
		...(pagination.hasMore !== undefined && { hasMore: pagination.hasMore }),
		...(pagination.total !== undefined && { total: pagination.total }),
	};
}

/**
 * API Client with methods for common HTTP operations
 */
export const apiClient = {
	/**
	 * DELETE request
	 * @param endpoint The API endpoint to call
	 * @param config Optional request configuration (can include skipAuth for public endpoints)
	 * @returns Promise with the response data and pagination information
	 */
	delete: async <T>(
		endpoint: string,
		config?: RequestConfig,
	): Promise<ApiResponse<T>> => {
		const response = await axiosInstance.delete<T>(endpoint, config);
		return createApiResponse<T>(response);
	},

	/**
	 * GET request
	 * @param endpoint The API endpoint to call
	 * @param config Optional request configuration (can include skipAuth for public endpoints)
	 * @returns Promise with the response data and pagination information
	 */
	get: async <T>(
		endpoint: string,
		config?: RequestConfig,
	): Promise<ApiResponse<T>> => {
		const response = await axiosInstance.get<T>(endpoint, config);
		return createApiResponse<T>(response);
	},

	/**
	 * PATCH request
	 * @param endpoint The API endpoint to call
	 * @param data Optional request body
	 * @param config Optional request configuration (can include skipAuth for public endpoints)
	 * @returns Promise with the response data and pagination information
	 */
	patch: async <T>(
		endpoint: string,
		data?: unknown,
		config?: RequestConfig,
	): Promise<ApiResponse<T>> => {
		const response = await axiosInstance.patch<T>(endpoint, data, config);
		return createApiResponse<T>(response);
	},

	/**
	 * POST request
	 * @param endpoint The API endpoint to call
	 * @param data Optional request body
	 * @param config Optional request configuration (can include skipAuth for public endpoints)
	 * @returns Promise with the response data and pagination information
	 */
	post: async <T>(
		endpoint: string,
		data?: unknown,
		config?: RequestConfig,
	): Promise<ApiResponse<T>> => {
		const response = await axiosInstance.post<T>(endpoint, data, config);
		return createApiResponse<T>(response);
	},

	/**
	 * PUT request
	 * @param endpoint The API endpoint to call
	 * @param data Optional request body
	 * @param config Optional request configuration (can include skipAuth for public endpoints)
	 * @returns Promise with the response data and pagination information
	 */
	put: async <T>(
		endpoint: string,
		data?: unknown,
		config?: RequestConfig,
	): Promise<ApiResponse<T>> => {
		const response = await axiosInstance.put<T>(endpoint, data, config);
		return createApiResponse<T>(response);
	},
};
